import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { initialMaquinasEquipListaFilters, useMaquinasEquipListaFilters } from "../hooks/MaquinasEquip_lista/filter";
import { ControlePatrimonioLista } from "../../domain/models/controle_patrimonio_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useMaquinasEquipListaPagination } from "../hooks/MaquinasEquip_lista/pagination";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

/**
 * LoadPatrimonioFuncState
 */

type LoadPatrimonioFuncState = {
  codigo?: string | null;
  descricao?: string | null;
  status?: number | null;
  localizacao?: string | null;
  centroCusto?: number[] | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useMaquinasEquipListaViewModel
 */

export const useMaquinasEquipListaViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
    },
    reports: {
      getReportMaquinasEquipUseCase,
    },
    controlePatrimonio: {
      getAllControlePatrimonioUseCase,
      deleteControlePatrimonioUseCase: deleteControledePatrimonioUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const debouncedLoad = useCreateDebounceHandler(750);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();

  const [controlePatrimonioLista, setControlePatrimonioLista] = useState<ControlePatrimonioLista[]>();
  const [selectedControlePatrimonio, setSelectedControlePatrimonio] = useState<ControlePatrimonioLista>();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const filters = useMaquinasEquipListaFilters();
  const pagination = useMaquinasEquipListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funcoes de carregamento
   */

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setCentroCustoDropdown(centroCusto);
    } catch (e: unknown) {
      setCentroCustoDropdown(undefined);
      throw new Error("Falha ao buscar centro de custo!");
    }
  }, [
    getCentroCustoDropDownUseCase,
  ]);

  const loadControlePatrimonios = useCallback(async (funcState?: LoadPatrimonioFuncState) => {
    try {
      const filterCodigo = evaluateParamValue(funcState?.codigo, filters.codigo);
      const filterDescricao = evaluateParamValue(funcState?.descricao, filters.descricao);
      const filterStatus = evaluateParamValue(funcState?.status, filters.status);
      const filterLocalizacao = evaluateParamValue(funcState?.localizacao, filters.localizacao);
      const filterCentroCusto = evaluateParamValue(funcState?.centroCusto, filters.centroCusto);

      const filterPage = funcState?.page ?? pagination.page;
      const filterRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllControlePatrimonioUseCase.getAllControlePatrimonio(
        filterCodigo,
        filterDescricao,
        filterStatus,
        filterLocalizacao,
        filterCentroCusto,
        filterPage,
        filterRowsPerPage,
      );

      setControlePatrimonioLista(getAllControlePatrimonioUseCase.items);
      pagination.setTotalItems(getAllControlePatrimonioUseCase.totalItems);
    } catch (e: unknown) {
      setControlePatrimonioLista([]);
      throw new Error("Falha ao buscar controle de patrimônio!");
    }
  }, [
    filters,
    pagination,
    getAllControlePatrimonioUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown();
      await loadControlePatrimonios();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados do controle de patrimônio!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown();

      await loadControlePatrimonios();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados do controle de patrimônio!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadControlePatrimonios,
    pushErrorNotification,
  ]);

  const handleCreate = useCallback(() => {
    backOrNavigate(`/Maquinasequip_cadastroprincipal`);
  }, [
    backOrNavigate,
  ]);

  const handleViewItens = useCallback((idChecklist: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/MaquinasEquipItens_cadastro?id=${idChecklist}`, "_blank", "noopener,noreferrer");
    } else {
      backOrNavigate(`/MaquinasEquipItens_cadastro?id=${idChecklist}`);
    }
  }, [
    backOrNavigate,
  ]);

  const handleEditarControlePatrimonio = useCallback((idChecklist: number | null, openState: boolean) => {
    if (openState) {
      backOrNavigate(`/Maquinasequip_cadastroprincipal?id=${idChecklist}`);
    }
  }, [
    backOrNavigate,
  ]);

  const handleReportMaquinasEquip = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportMaquinasEquipUseCase.getReportMaquinasEquip(requiredMimeType),
      });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao gerar relatorio de ordem de compra!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportMaquinasEquipUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useMaquinasEquipListaPagination.setState({ page: page + 1 });
      await loadControlePatrimonios({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadControlePatrimonios,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useMaquinasEquipListaPagination.setState({ page: 1, rowsPerPage });
      await loadControlePatrimonios({ page: 1, rowsPerPage: rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
    loadControlePatrimonios,
  ]);

  const handleCodigoChange = useCallback(async (novoCodigo: string | null) => {
    try {
      useMaquinasEquipListaFilters.getState().setCodigo(novoCodigo);
      await loadControlePatrimonios({ codigo: novoCodigo ?? "" });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    }
  }, [
    loadControlePatrimonios,
    pushErrorNotification
  ]);

  const handleDescricaoChange = useDebouncedCallback(debouncedLoad, (novaDescricao: string | null) => {
    useMaquinasEquipListaFilters.getState().setDescricao(novaDescricao);
    return async () => {
      try {
        await loadControlePatrimonios({ descricao: novaDescricao ?? "" });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      }
    };
  }, [
    loadControlePatrimonios,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number | null) => {
    try {
      useMaquinasEquipListaFilters.getState().setStatus(status);
      await loadControlePatrimonios({ status });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    }
  }, [
    loadControlePatrimonios,
    pushErrorNotification,
  ]);

  const handleLocalizacaoChange = useDebouncedCallback(debouncedLoad, (novaLocalizacao: string | null) => {
    useMaquinasEquipListaFilters.getState().setLocalizacao(novaLocalizacao);
    return async () => {
      try {
        await loadControlePatrimonios({ localizacao: novaLocalizacao ?? "" });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      }
    };
  }, [
    loadControlePatrimonios,
    pushErrorNotification
  ]);

  const handleCentroCustoChange = useCallback(async (novoCentroCusto: number[] | null) => {
    try {
      useMaquinasEquipListaFilters.getState().setCentroCusto(novoCentroCusto);
      await loadControlePatrimonios({ centroCusto: novoCentroCusto });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
    }
  }, [
    loadControlePatrimonios,
    pushErrorNotification,
  ]);

  const handleClear = useCallback(async () => {
    try {
      useMaquinasEquipListaFilters.getState().clear();
      await loadControlePatrimonios({ ...initialMaquinasEquipListaFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao atualizar página!"));
    }
  }, [
    loadControlePatrimonios,
    pushErrorNotification,
  ]);

  const handleDeletarControlePatrimonio = useCallback(async () => {
    if (selectedControlePatrimonio) {
      try {
        setSaving(true);

        await deleteControledePatrimonioUseCase.deleteControlePatrimonio(selectedControlePatrimonio.idMaquinaEquip);
        await loadControlePatrimonios();

        setOpenDeleteModal(false);

        pushInfoNotification("Sucesso", "Item excluído com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir item!"));
      } finally {
        setSaving(false);
      }
    }
  }, [
    selectedControlePatrimonio,
    deleteControledePatrimonioUseCase,
    loadControlePatrimonios,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleDeleteOpenModal = useCallback((idControlePatrimonio: number | null, openState: boolean) => {
    if (openState) {
      if (idControlePatrimonio !== null && controlePatrimonioLista) {
        const controle = controlePatrimonioLista.find(item => item.idMaquinaEquip === idControlePatrimonio);
        setSelectedControlePatrimonio(controle);
      } else {
        setSelectedControlePatrimonio(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [
    controlePatrimonioLista,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    centroCustoDropdown: centroCustoDropdown ?? [],
    controlePatrimonioLista: controlePatrimonioLista ?? [],
    selectedControlePatrimonio: selectedControlePatrimonio ?? null,
    openDeleteModal,
    filters,
    pagination,
    reportViewerState,
    handleReload,
    handleCreate,
    handleViewItens,
    handleEditarControlePatrimonio,
    handleReportMaquinasEquip,
    handlePageChange,
    handleRowsPerPageChange,
    handleCodigoChange,
    handleDescricaoChange,
    handleStatusChange,
    handleLocalizacaoChange,
    handleCentroCustoChange,
    handleDeleteOpenModal,
    handleDeletarControlePatrimonio,
    handleClear,
  };
};
